// Pages « événement de vie » : tout ce que la boutique propose pour un mariage, une naissance, un
// baptême ou une communion, un anniversaire — cartes à gratter ET faire-part / invitations animés.
// Elles portent les mots-clés d'événement (« faire-part de mariage », « faire-part de naissance »…)
// et alimentent le menu déroulant de l'en-tête. Les pages plus précises, une par produit animé,
// sont décrites dans minisiteOccasions.js (`hub`).
//
// `occasions` : clés de minisiteOccasions.js affichées sur la page. `cartes` : les cartes à gratter
// rattachées à l'événement, reconnues par leur adresse ou leur titre (pas de liste en dur : une
// nouvelle carte « parrain / marraine » créée dans l'admin apparaît toute seule sur la bonne page).
//
// `menu` : les entrées du menu déroulant de l'événement dans l'en-tête (Header.js) — chaque produit
// vendu pour cet événement, puis la page qui réunit tout.
import { useMemo } from 'react';
import { useT } from '../i18n';
import FR_MENUS from '../i18n/locales/fr/life-events.json';
import FR_PAGES from '../i18n/locales/fr/life-event-pages.json';

export const CARTES_PATH = '/cartes-a-gratter-personnalisees';

// Textes des MENUS (label de l'événement, son titre, entrées et leurs sous-titres, « Tout pour… ») : ils vivent
// dans src/i18n/locales/<langue>/life-events.json — le français y est la source unique. Ici on ne garde
// que la structure (adresses, identifiants) ; LIFE_EVENTS reste un objet FRANÇAIS complet, comme avant,
// pour les composants pas encore convertis. Les composants traduits utilisent useLifeEvents().
// Les textes des PAGES (méta, intro, cartes à gratter, articles) vivent dans locales/<langue>/life-event-pages.json ; comme pour
// les menus, le français y est la source unique et LIFE_EVENTS reste un objet français complet. Les pages traduites
// passent par useLifeEventPage().
const menuItem = (event, id, to) => ({ id, to, label: FR_MENUS[event].menu[id].label, hint: FR_MENUS[event].menu[id].hint });

export const LIFE_EVENTS = [
  {
    key: 'mariage',
    path: '/faire-part-mariage',
    image: '/assets_react/minisites/faire-part-mariage-anime-fleurs-bleues-aquarelle.jpg',
    label: FR_MENUS['mariage'].label,
    menu: [
      menuItem('mariage', 'fairePart', '/faire-part-mariage-anime'),
      menuItem('mariage', 'saveTheDate', '/save-the-date-anime'),
      menuItem('mariage', 'cartes', `${CARTES_PATH}?theme=mariage`),
      menuItem('mariage', 'galerie', '/produit/galerie-photos-invites-mariage'),
    ],
    menuTout: FR_MENUS['mariage'].menuTout,
    titre: FR_MENUS['mariage'].titre,
    metaTitle: FR_PAGES['mariage'].metaTitle,
    metaDescription: FR_PAGES['mariage'].metaDescription,
    intro: FR_PAGES['mariage'].intro,
    occasions: ['mariage', 'save-the-date'],
    cartes: /mariage|t[ée]moin|save-the-date|evjf|fian[çc]/i,
    cartesTitre: FR_PAGES['mariage'].cartesTitre,
    cartesTexte: FR_PAGES['mariage'].cartesTexte,
    galerie: true,
    seo: [
      { id: 'digital', titre: FR_PAGES['mariage'].seo.digital.titre, texte: FR_PAGES['mariage'].seo.digital.texte },
      { id: 'order', titre: FR_PAGES['mariage'].seo.order.titre, texte: FR_PAGES['mariage'].seo.order.texte },
    ],
  },
  {
    key: 'naissance',
    path: '/faire-part-naissance',
    image: '/assets_react/minisites/faire-part-naissance-anime-ourson-ballons-roses.jpg',
    label: FR_MENUS['naissance'].label,
    menu: [
      menuItem('naissance', 'fairePart', '/faire-part-naissance-anime'),
      menuItem('naissance', 'grossesse', `${CARTES_PATH}?theme=grossesse`),
      menuItem('naissance', 'cartes', `${CARTES_PATH}?theme=naissance`),
    ],
    menuTout: FR_MENUS['naissance'].menuTout,
    titre: FR_MENUS['naissance'].titre,
    metaTitle: FR_PAGES['naissance'].metaTitle,
    metaDescription: FR_PAGES['naissance'].metaDescription,
    intro: FR_PAGES['naissance'].intro,
    occasions: ['naissance'],
    cartes: /grossesse|gender|naissance|b[ée]b[ée]/i,
    cartesTitre: FR_PAGES['naissance'].cartesTitre,
    cartesTexte: FR_PAGES['naissance'].cartesTexte,
    seo: [
      { id: 'hospital', titre: FR_PAGES['naissance'].seo.hospital.titre, texte: FR_PAGES['naissance'].seo.hospital.texte },
      { id: 'pregnancy', titre: FR_PAGES['naissance'].seo.pregnancy.titre, texte: FR_PAGES['naissance'].seo.pregnancy.texte },
    ],
  },
  {
    key: 'bapteme-communion',
    path: '/invitation-bapteme-communion',
    image: '/assets_react/minisites/invitation-bapteme-animee-eucalyptus-or-champetre.jpg',
    label: FR_MENUS['bapteme-communion'].label,
    menu: [
      menuItem('bapteme-communion', 'bapteme', '/invitation-bapteme-animee'),
      menuItem('bapteme-communion', 'communion', '/invitation-communion-animee'),
      menuItem('bapteme-communion', 'parrain', `${CARTES_PATH}?theme=marraine`),
    ],
    menuTout: FR_MENUS['bapteme-communion'].menuTout,
    titre: FR_MENUS['bapteme-communion'].titre,
    metaTitle: FR_PAGES['bapteme-communion'].metaTitle,
    metaDescription: FR_PAGES['bapteme-communion'].metaDescription,
    intro: FR_PAGES['bapteme-communion'].intro,
    occasions: ['bapteme', 'communion'],
    cartes: /bapt[êe]me|parrain|marraine|communion/i,
    cartesTitre: FR_PAGES['bapteme-communion'].cartesTitre,
    cartesTexte: FR_PAGES['bapteme-communion'].cartesTexte,
    seo: [
      { id: 'accepted', titre: FR_PAGES['bapteme-communion'].seo.accepted.titre, texte: FR_PAGES['bapteme-communion'].seo.accepted.texte },
      { id: 'meal', titre: FR_PAGES['bapteme-communion'].seo.meal.titre, texte: FR_PAGES['bapteme-communion'].seo.meal.texte },
    ],
  },
  {
    key: 'anniversaire',
    path: '/invitation-anniversaire',
    image: '/assets_react/minisites/invitation-anniversaire-animee-40-ans-noir-et-or.jpg',
    label: FR_MENUS['anniversaire'].label,
    menu: [
      menuItem('anniversaire', 'adulte', '/invitation-anniversaire-animee'),
      menuItem('anniversaire', 'enfant', '/invitation-anniversaire-enfant-animee'),
      menuItem('anniversaire', 'surprise', `${CARTES_PATH}?theme=surprise`),
    ],
    menuTout: FR_MENUS['anniversaire'].menuTout,
    titre: FR_MENUS['anniversaire'].titre,
    metaTitle: FR_PAGES['anniversaire'].metaTitle,
    metaDescription: FR_PAGES['anniversaire'].metaDescription,
    intro: FR_PAGES['anniversaire'].intro,
    occasions: ['anniversaire', 'anniversaire-enfant'],
    cartes: /anniversaire/i,
    cartesTitre: FR_PAGES['anniversaire'].cartesTitre,
    cartesTexte: FR_PAGES['anniversaire'].cartesTexte,
    seo: [
      { id: 'whatsapp', titre: FR_PAGES['anniversaire'].seo.whatsapp.titre, texte: FR_PAGES['anniversaire'].seo.whatsapp.texte },
      { id: 'child', titre: FR_PAGES['anniversaire'].seo.child.titre, texte: FR_PAGES['anniversaire'].seo.child.texte },
    ],
  },
];

export const getLifeEvent = (key) => LIFE_EVENTS.find((e) => e.key === key);

// Événements de vie dans la langue courante : label, entrées de menu (label + sous-titre) et « Tout pour… »
// traduits ; le reste de l'objet est inchangé. Clés : life-events.<événement>.label, .titre, .menuTout,
// .menu.<identifiant>.label et .hint. En français : exactement les textes de LIFE_EVENTS.
export const useLifeEvents = () => {
  const t = useT('life-events');
  return useMemo(() => LIFE_EVENTS.map((event) => ({
    ...event,
    label: t(`life-events.${event.key}.label`),
    titre: t(`life-events.${event.key}.titre`),
    menuTout: t(`life-events.${event.key}.menuTout`),
    menu: event.menu.map((item) => ({
      ...item,
      label: t(`life-events.${event.key}.menu.${item.id}.label`),
      hint: t(`life-events.${event.key}.menu.${item.id}.hint`),
    })),
  })), [t]);
};

// Un événement (page /faire-part-mariage…) dans la langue courante : titre, méta, introduction, cartes à gratter et
// articles de référencement traduits ; le reste de l'objet (adresse, image, filtre des cartes, occasions) est inchangé.
// Clés : life-events.<événement>.titre (menus, socle) et life-event-pages.<événement>.* . À appeler en haut du composant.
export const useLifeEventPage = (key) => {
  const t = useT('life-events', 'life-event-pages');
  return useMemo(() => {
    const event = getLifeEvent(key);
    if (!event) return null;
    const p = (champ) => t(`life-event-pages.${key}.${champ}`);
    return {
      ...event,
      titre: t(`life-events.${key}.titre`),
      metaTitle: p('metaTitle'),
      metaDescription: p('metaDescription'),
      intro: p('intro'),
      cartesTitre: p('cartesTitre'),
      cartesTexte: p('cartesTexte'),
      seo: event.seo.map(({ id }) => ({ id, titre: p(`seo.${id}.titre`), texte: p(`seo.${id}.texte`) })),
    };
  }, [t, key]);
};
